import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { DrawingThumbnail, drawingThumbnailSvg } from '../../components/DrawingThumbnail';
import { Icon, type IconName } from '../../components/Icon';
import { loadPublicDrawing, type PublicDrawing } from './publicDrawing';
import { PublicLayout } from './PublicLayout';
import { SignInPrompt } from './SignInPrompt';
import { DiscussionThread } from '../discussions/DiscussionThread';
import { moleculePagePath, publicDrawingPath, usePublicAccess } from './publicAccess';
import { CatalogueMeta } from './CatalogueMeta';
import { drawingHead } from './scientificMeta';

type PageState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly value: PublicDrawing }
  | { readonly status: 'notFound' };

/** The actions only signed-in users can take; signed out, each asks them to sign in (#632). */
type GatedAction = 'addToPaper' | 'export' | 'share';

const COPIED_FEEDBACK_MS = 2000;

function fileNameFor(title: string | null): string {
  const base = (title ?? '')
    .trim()
    .replace(/[^A-Za-z0-9._-]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return `${base || 'drawing'}.svg`;
}

/**
 * #885: one public drawing -- structure, title, author, SMILES. Anyone may view it and copy its
 * SMILES; adding it to a paper, downloading it and sharing it need signing in (owner decision #632:
 * no copy of the artifact itself signed out), and those buttons ask for it rather than vanish.
 */
export function PublicDrawingPage() {
  const { t, i18n } = useTranslation();
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { signedIn, signIn, signUp } = usePublicAccess();
  const [state, setState] = useState<PageState>({ status: 'loading' });
  const [prompt, setPrompt] = useState<GatedAction | null>(null);
  const [copied, setCopied] = useState<'smiles' | 'link' | null>(null);

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading' });
    loadPublicDrawing(id)
      .then((value) => {
        if (!cancelled) setState({ status: 'ready', value });
      })
      .catch(() => {
        if (!cancelled) setState({ status: 'notFound' });
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  useEffect(() => {
    if (!copied) return;
    const timer = globalThis.setTimeout(() => setCopied(null), COPIED_FEEDBACK_MS);
    return () => globalThis.clearTimeout(timer);
  }, [copied]);

  const copy = async (text: string, what: 'smiles' | 'link') => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(what);
    } catch {
      setCopied(null);
    }
  };

  const run = async (action: GatedAction, drawing: PublicDrawing['drawing']) => {
    if (!signedIn) {
      setPrompt(action);
      return;
    }
    if (action === 'addToPaper') {
      navigate(`/draw?addDrawing=${encodeURIComponent(drawing.id)}`);
    } else if (action === 'share') {
      await copy(window.location.href, 'link');
    } else {
      const svg = await drawingThumbnailSvg(drawing.id, drawing.updatedAt, 'paper');
      const url = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml' }));
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = fileNameFor(drawing.title);
      anchor.click();
      URL.revokeObjectURL(url);
    }
  };

  const gatedButtons: readonly { readonly action: GatedAction; readonly icon: IconName; readonly label: string }[] = [
    { action: 'addToPaper', icon: 'add', label: t('publicPages.addToPaper') },
    { action: 'export', icon: 'download', label: t('publicPages.downloadSvg') },
    {
      action: 'share',
      icon: 'link',
      label: copied === 'link' ? t('publicPages.linkCopied') : t('publicPages.share')
    }
  ];

  return (
    <PublicLayout>
      <Link to="/public" className="public-back">
        <Icon name="arrowBack" />
        <span>{t('publicPages.back')}</span>
      </Link>
      {state.status === 'loading' ? <p role="status">{t('common.loading')}</p> : null}
      {state.status === 'notFound' ? (
        <p className="import-error" role="alert">
          {t('publicPages.notFound')}
        </p>
      ) : null}
      {state.status === 'ready' ? (
        <article className="public-drawing" aria-labelledby="public-drawing-title">
          {/* #1123 (ADR 0023): what search engines and link previews read, at /i/<id>. */}
          <CatalogueMeta
            head={drawingHead(t, {
              id: state.value.drawing.id,
              title: state.value.drawing.title,
              author: state.value.drawing.authorName ?? null,
              updatedAt: state.value.drawing.updatedAt,
              formula: state.value.formula,
              smiles: state.value.smiles
            })}
          />
          <header>
            <h1 id="public-drawing-title">{state.value.drawing.title || t('publicPages.untitled')}</h1>
            <p className="public-drawing-meta">
              {t('publicPages.byAuthor', {
                author: state.value.drawing.authorName || t('publicPages.unknownAuthor')
              })}
              {' · '}
              {t('publicPages.updated', {
                date: new Date(state.value.drawing.updatedAt).toLocaleDateString(i18n.language, {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric'
                })
              })}
            </p>
          </header>
          <figure className="public-drawing-figure">
            <DrawingThumbnail
              drawingId={state.value.drawing.id}
              version={state.value.drawing.updatedAt}
              ink="paper"
              fill
            />
          </figure>
          <div className="public-drawing-smiles">
            <span className="public-drawing-smiles-label">{t('publicPages.smilesLabel')}</span>
            {state.value.smiles ? (
              <>
                <code>{state.value.smiles}</code>
                <button
                  type="button"
                  className="icon-button"
                  onClick={() => void copy(state.value.smiles ?? '', 'smiles')}
                  aria-label={copied === 'smiles' ? t('publicPages.copied') : t('publicPages.copySmiles')}
                  title={copied === 'smiles' ? t('publicPages.copied') : t('publicPages.copySmiles')}
                >
                  <Icon name={copied === 'smiles' ? 'check' : 'copy'} />
                </button>
                {/* ADR 0026 Q1 (#1162): as Structure info says it in the editor. */}
                {state.value.haptoBonds ? <p className="smiles-hapto-note">{t('drawingInfo.haptoSmiles')}</p> : null}
              </>
            ) : (
              <span className="public-drawing-smiles-missing">{t('publicPages.smilesUnavailable')}</span>
            )}
            {/* ADR 0017 D9 (#904): the molecule's factsheet. */}
            {state.value.moleculeId ? (
              <Link
                to={moleculePagePath(state.value.moleculeId, signedIn)}
                className="icon-button public-drawing-molecule-link"
                aria-label={t('moleculePage.link')}
                title={t('moleculePage.link')}
              >
                <Icon name="info" />
              </Link>
            ) : null}
          </div>
          <div className="public-drawing-actions">
            {gatedButtons.map(({ action, icon, label }) => (
              <button
                key={action}
                type="button"
                className="icon-button"
                onClick={() => void run(action, state.value.drawing)}
                aria-label={label}
                title={label}
              >
                <Icon name={icon} />
                {signedIn ? null : <Icon name="lock" size="0.95rem" />}
              </button>
            ))}
          </div>
          {/* ADR 0016: the drawing's discussion -- read-only signed out (D3). */}
          <DiscussionThread kind="molecule-drawing" artifactId={state.value.drawing.id} />
        </article>
      ) : null}
      {prompt ? (
        <SignInPrompt
          message={t(`publicPages.prompt.${prompt}`)}
          onSignIn={() => signIn(location.pathname + location.search)}
          onSignUp={() => signUp(location.pathname + location.search)}
          onClose={() => setPrompt(null)}
        />
      ) : null}
    </PublicLayout>
  );
}

/** #1123 (ADR 0023): the old public address, kept for links already shared; the catalogue's is `/i/<id>`. */
export function LegacyPublicDrawingRedirect() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={id ? publicDrawingPath(id) : '/public'} replace />;
}
